import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getVaultDocuments, getVaultFreshness } from '../services/ea-api'
import EAVendorBadge from '../components/ea/EAVendorBadge'
import EAVaultFreshnessBar from '../components/ea/EAVaultFreshnessBar'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const ALL_VENDORS = ['Arista', 'PaloAlto', 'Nutanix', 'Azure', 'Cisco']

const VENDOR_COLORS: Record<string, string> = {
  Arista: '#2563eb',
  PaloAlto: '#dc2626',
  Nutanix: '#059669',
  Azure: '#0284c7',
  Cisco: '#7c3aed',
}

const VERIFICATION_COLORS: Record<string, string> = {
  verified: '#10b981',
  unverified: '#f59e0b',
  outdated: '#ef4444',
  pending: '#94a3b8',
}

const VERIFICATION_LABELS: Record<string, string> = {
  verified: 'Verified',
  unverified: 'Unverified',
  outdated: 'Outdated',
  pending: 'Pending',
}

const RISK_COLORS: Record<string, string> = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#ef4444',
  critical: '#dc2626',
}

export default function EAVault() {
  const [search, setSearch] = useState('')
  const [vendorFilter, setVendorFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [staleOnly, setStaleOnly] = useState(false)
  const [sortBy, setSortBy] = useState('updated')

  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: ['ea-vault-docs', vendorFilter, categoryFilter, staleOnly],
    queryFn: () =>
      getVaultDocuments({
        vendor: vendorFilter !== 'all' ? vendorFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        stale_only: staleOnly || undefined,
      }).then((res) => res.data),
  })

  const freshnessQueries = ALL_VENDORS.map((vendor) => ({
    vendor,
    ...useQuery({
      queryKey: ['ea-vault-freshness', vendor],
      queryFn: () => getVaultFreshness(vendor).then((res) => res.data),
    }),
  }))

  const docs = Array.isArray(docsData) ? docsData : []

  // Compute totals from freshness reports
  const freshnessTotals = useMemo(() => {
    let totalDocs = 0
    let staleDocs = 0
    let freshDocs = 0
    freshnessQueries.forEach((q) => {
      if (q.data) {
        totalDocs += q.data.total_docs
        staleDocs += q.data.stale_count
        freshDocs += q.data.fresh_count
      }
    })
    return { totalDocs, staleDocs, freshDocs }
  }, [freshnessQueries.map((q) => q.data)])

  // Extract unique categories from loaded docs
  const categories = useMemo(() => {
    const cats = new Set<string>()
    docs.forEach((d) => cats.add(d.category))
    return Array.from(cats).sort()
  }, [docs])

  const filteredDocs = useMemo(() => {
    let filtered = docs.filter((d) =>
      d.title.toLowerCase().includes(search.toLowerCase())
    )

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title)
        case 'vendor':
          return a.vendor.localeCompare(b.vendor)
        case 'fetched':
          return new Date(b.fetched_date).getTime() - new Date(a.fetched_date).getTime()
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })

    return filtered
  }, [docs, search, sortBy])

  if (docsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 0 30px rgba(139, 92, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#a78bfa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading vault documents...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Vendor Vault</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            Browse and manage vendor documentation and best practices
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search documents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-80 pl-10 pr-4 py-2.5 rounded-xl text-sm"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Search vault documents"
            />
            <i
              className="fa-solid fa-search absolute left-3 top-3"
              style={{ color: 'rgba(148, 163, 184, 0.5)' }}
            ></i>
          </div>
          <Link
            to="/ea/vault/new"
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.4)',
              color: '#e2e8f0',
            }}
          >
            <i className="fa-solid fa-plus"></i>
            <span>Add Document</span>
          </Link>
        </div>
      </div>

      {/* Freshness Summary */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 items-center">
          <div className="lg:col-span-2">
            <EAVaultFreshnessBar
              totalDocs={freshnessTotals.totalDocs}
              staleDocs={freshnessTotals.staleDocs}
              freshDocs={freshnessTotals.freshDocs}
            />
          </div>
          <div className="lg:col-span-4">
            <div className="grid grid-cols-5 gap-3">
              {ALL_VENDORS.map((vendor) => {
                const q = freshnessQueries.find((fq) => fq.vendor === vendor)
                const data = q?.data
                const color = VENDOR_COLORS[vendor]
                return (
                  <div
                    key={vendor}
                    className="rounded-lg p-3 text-center"
                    style={{
                      background: `${color}11`,
                      border: `1px solid ${color}33`,
                    }}
                  >
                    <p className="text-xs font-semibold mb-1" style={{ color }}>
                      {vendor}
                    </p>
                    <p className="text-lg font-bold" style={{ color: '#e2e8f0' }}>
                      {data?.total_docs ?? '-'}
                    </p>
                    <div className="flex items-center justify-center space-x-1 mt-1">
                      <EAVaultFreshnessBar
                        totalDocs={data?.total_docs ?? 0}
                        staleDocs={data?.stale_count ?? 0}
                        freshDocs={data?.fresh_count ?? 0}
                        variant="compact"
                      />
                      <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                        {data ? `${data.fresh_count}/${data.total_docs}` : '-'}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Filter Tabs */}
      <div className="flex items-center space-x-2 flex-wrap gap-y-2">
        <button
          onClick={() => setVendorFilter('all')}
          className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200"
          style={{
            background: vendorFilter === 'all' ? 'rgba(139, 92, 246, 0.25)' : 'rgba(30, 41, 59, 0.5)',
            border: `1px solid ${vendorFilter === 'all' ? 'rgba(139, 92, 246, 0.5)' : 'rgba(100, 180, 255, 0.15)'}`,
            color: vendorFilter === 'all' ? '#a78bfa' : 'rgba(148, 163, 184, 0.7)',
          }}
        >
          All Vendors
        </button>
        {ALL_VENDORS.map((vendor) => {
          const color = VENDOR_COLORS[vendor]
          const isActive = vendorFilter === vendor
          return (
            <button
              key={vendor}
              onClick={() => setVendorFilter(vendor)}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200"
              style={{
                background: isActive ? `${color}33` : 'rgba(30, 41, 59, 0.5)',
                border: `1px solid ${isActive ? `${color}66` : 'rgba(100, 180, 255, 0.15)'}`,
                color: isActive ? color : 'rgba(148, 163, 184, 0.7)',
              }}
            >
              {vendor}
            </button>
          )
        })}
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Filters:
            </span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Filter by category"
            >
              <option value="all">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <label
              className="flex items-center space-x-2 cursor-pointer"
              style={{ color: 'rgba(148, 163, 184, 0.7)' }}
            >
              <input
                type="checkbox"
                checked={staleOnly}
                onChange={(e) => setStaleOnly(e.target.checked)}
                className="rounded"
                style={{ accentColor: '#ef4444' }}
              />
              <span className="text-sm">Stale only</span>
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              Sort by:
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Sort documents"
            >
              <option value="updated">Last Updated</option>
              <option value="title">Title</option>
              <option value="vendor">Vendor</option>
              <option value="fetched">Fetched Date</option>
            </select>
            <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
              {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Document Cards Grid */}
      {filteredDocs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocs.map((doc) => {
            const verColor = VERIFICATION_COLORS[doc.verification_status] || '#94a3b8'
            const verLabel = VERIFICATION_LABELS[doc.verification_status] || doc.verification_status
            const riskColor = doc.risk_level ? RISK_COLORS[doc.risk_level] : undefined

            return (
              <Link
                key={doc.id}
                to={`/ea/vault/${doc.id}`}
                className="rounded-xl transition-all duration-300 hover:scale-[1.02] group cursor-pointer"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: `1px solid ${doc.is_stale ? 'rgba(239, 68, 68, 0.3)' : 'rgba(100, 180, 255, 0.15)'}`,
                }}
              >
                <div className="p-6">
                  {/* Top row: vendor badge + freshness dot */}
                  <div className="flex items-center justify-between mb-3">
                    <EAVendorBadge vendor={doc.vendor} size="md" />
                    <div className="flex items-center space-x-2">
                      {doc.is_stale ? (
                        <span
                          className="px-2 py-0.5 text-xs font-medium rounded-full"
                          style={{
                            background: 'rgba(239, 68, 68, 0.15)',
                            color: '#f87171',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                          }}
                        >
                          Stale
                        </span>
                      ) : (
                        <span
                          className="px-2 py-0.5 text-xs font-medium rounded-full"
                          style={{
                            background: 'rgba(16, 185, 129, 0.15)',
                            color: '#34d399',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                          }}
                        >
                          Fresh
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Title */}
                  <h3
                    className="text-base font-bold mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors"
                    style={{ color: '#e2e8f0' }}
                  >
                    {doc.title}
                  </h3>

                  {/* Category + subcategory */}
                  <p className="text-xs mb-3" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                    {doc.category}
                    {doc.subcategory && ` / ${doc.subcategory}`}
                  </p>

                  {/* Tags */}
                  {doc.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {doc.tags.slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs rounded-md"
                          style={{
                            background: 'rgba(100, 116, 139, 0.15)',
                            color: 'rgba(148, 163, 184, 0.7)',
                            border: '1px solid rgba(100, 116, 139, 0.2)',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                      {doc.tags.length > 4 && (
                        <span
                          className="px-2 py-0.5 text-xs rounded-md"
                          style={{ color: 'rgba(148, 163, 184, 0.5)' }}
                        >
                          +{doc.tags.length - 4}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Footer: verification + risk + date */}
                  <div
                    className="flex items-center justify-between pt-3"
                    style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}
                  >
                    <div className="flex items-center space-x-2">
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{
                          background: `${verColor}22`,
                          color: verColor,
                          border: `1px solid ${verColor}44`,
                        }}
                      >
                        {verLabel}
                      </span>
                      {riskColor && (
                        <span
                          className="px-2 py-0.5 text-xs font-medium rounded-full"
                          style={{
                            background: `${riskColor}22`,
                            color: riskColor,
                            border: `1px solid ${riskColor}44`,
                          }}
                        >
                          {doc.risk_level}
                        </span>
                      )}
                    </div>
                    <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      {new Date(doc.fetched_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
            }}
          >
            <i
              className="fa-solid fa-vault text-3xl"
              style={{ color: 'rgba(167, 139, 250, 0.5)' }}
            ></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            {search || vendorFilter !== 'all' || categoryFilter !== 'all' || staleOnly
              ? 'No documents match your filters'
              : 'No vault documents yet'}
          </h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            {search || vendorFilter !== 'all' || categoryFilter !== 'all' || staleOnly
              ? 'Try adjusting your search or filter criteria'
              : 'Add vendor documentation to get started'}
          </p>
          {!search && vendorFilter === 'all' && categoryFilter === 'all' && !staleOnly && (
            <Link
              to="/ea/vault/new"
              className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 inline-flex items-center space-x-2 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                color: '#e2e8f0',
              }}
            >
              <i className="fa-solid fa-plus"></i>
              <span>Add your first vault document</span>
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
